// Menu → Costing → "Sell as-is" (kitchen flow, Step 4): turn a stock item sold
// one for one (a can of drink) into a menu product with a one-line recipe of
// qty 1 — the existing product create with `components`. Shown only with the
// kitchen flow on, for admin (menu writes are admin's).
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useCategories } from '@/hooks/useCategories'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import { toastHelpers } from '@/lib/toast-helpers'
import { SA, canSellAsIs, menuNameFor, sellAsIsPayload } from '@/lib/inventoryFlow/sellAsIs'
import type { FlowItem } from '@/lib/inventoryFlow/types'

export function SellAsIsCard() {
  const on = useKitchenFlowEnabled()
  const [open, setOpen] = useState(false)
  if (!on || getStoredUserRole() !== 'admin') return null
  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium">{SA.title}</p>
            <p className="text-sm text-muted-foreground">{SA.help}</p>
          </div>
          <Button variant="outline" onClick={() => setOpen((o) => !o)}>{SA.open}</Button>
        </div>
        {open && <SellAsIsForm />}
      </CardContent>
    </Card>
  )
}

function SellAsIsForm() {
  const qc = useQueryClient()
  const { data: itemsRes } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), staleTime: 60_000 })
  const { data: categories = [] } = useCategories()
  const [search, setSearch] = useState('')
  const [item, setItem] = useState<FlowItem | null>(null)
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [busy, setBusy] = useState(false)
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return (itemsRes?.data ?? []).filter((i) => !i.is_prep && i.name.toLowerCase().includes(q)).slice(0, 15)
  }, [itemsRes, search])
  const pick = (i: FlowItem) => {
    setItem(i)
    setName(menuNameFor(i.name))
    setSearch('')
  }
  const create = async () => {
    if (!item) return
    setBusy(true)
    try {
      await apiClient.createProduct(sellAsIsPayload({ stockItemId: item.id, unit: item.unit, name, price: Number(price), categoryId: categoryId || undefined }))
      toastHelpers.success(SA.created(name))
      qc.invalidateQueries({ queryKey: ['products'] })
      setItem(null)
      setName('')
      setPrice('')
    } catch (e) {
      toastHelpers.error(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }
  if (!item) {
    return (
      <div className="space-y-2">
        <Input placeholder={SA.search} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          {search.trim() && matches.length === 0 && <p className="text-sm text-muted-foreground">{SA.none}</p>}
          {matches.map((i) => (
            <button key={i.id} type="button" className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted" onClick={() => pick(i)}>
              {i.name} <span className="text-muted-foreground">({i.unit})</span>
            </button>
          ))}
        </div>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[12rem]">
        <p className="font-medium">{item.name}</p>
        <p className="text-xs text-muted-foreground">{SA.recipe(item.unit)}</p>
      </div>
      <label className="space-y-1"><span className="text-sm">{SA.name}</span><Input className="w-56" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="space-y-1"><span className="text-sm">{SA.price}</span><Input type="number" min={0} step="0.01" className="w-32" value={price} onChange={(e) => setPrice(e.target.value)} /></label>
      <label className="space-y-1"><span className="text-sm">{SA.category}</span>
        <select className="h-10 rounded-md border bg-background px-2 text-sm" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <Button disabled={!canSellAsIs({ name, price: Number(price) }) || busy} onClick={() => void create()}>{busy ? SA.creating : SA.create}</Button>
      <Button variant="ghost" onClick={() => setItem(null)}>✕</Button>
    </div>
  )
}
